import { Button, GlobalContext } from 'lynkco-up-core';
import React, { useContext, useEffect, useState } from 'react';
import { getComments, postComment } from '../../modules/Colab/commentService';
import { ColabComment, GetColab } from '../../modules/Colab/types';
import { formatDateTimeCompact } from '../shared/utils';
import { LoadingMask } from '../../components';

type ColabEntityProp = { colabEntity?: GetColab };

function colabComments({ colabEntity }: ColabEntityProp) {
  const [comment, setComment] = useState('');
  const maxLength = Number(process.env.REACT_APP_COLAB_COMMENT_MAX_LENGTH) || 200;
  const [isMaxLengthExceeded, setIsMaxLengthExceeded] = useState(false);
  const [commentList, setCommentList] = useState<ColabComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { alert } = useContext(GlobalContext);

  const feedbackRowKey = colabEntity?.rowKey;

  async function fetchData() {
    try {
      setIsLoading(true);
      const comments = await getComments(feedbackRowKey);
      const sortedComments = comments.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      setCommentList(sortedComments);
    } catch (error) {
      console.error('Error occurred while fetching comments: ', error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (colabEntity?.rowKey) {
      fetchData();
    }
  }, [colabEntity?.rowKey]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!comment.trim() || !colabEntity?.partitionKey || !colabEntity?.rowKey) {
      alert.show('Comment field empty', 'error');
      return;
    }
    postComment(colabEntity.partitionKey, colabEntity.rowKey, comment)
      .then(() => {
        setComment('');
        setIsMaxLengthExceeded(false);
        fetchData();
      })
      .catch(error => {
        console.log('Failed to submit comment:', error);
      });
  }

  const handleCommentTyping = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newComment = event.target.value;
    setComment(newComment);
    newComment.length >= maxLength ? setIsMaxLengthExceeded(true) : setIsMaxLengthExceeded(false);
  };

  const remainingCharacters = maxLength - comment.length;

  return (
    <div className="min-h-screen">
      <div className="container mx-auto p-8">
        <h3 className="font-medium text-xl mb-7">Comments</h3>
        <form className="mt-8" onSubmit={handleSubmit}>
          <textarea
            name="comment"
            placeholder="Your Comment"
            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={comment}
            onChange={handleCommentTyping}
            maxLength={maxLength}
            rows={8}
          />
          <div className="flex items-center justify-between text-sm text-gray-500 mt-1">
            <span className={isMaxLengthExceeded ? 'text-red-500' : 'invisible'}>Maximum characters used.</span>
            <span>
              {remainingCharacters}/{maxLength}
            </span>
          </div>
          <Button
            type="submit"
            variant="primary"
            extraClasses="mt-4 px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500">
            Submit Comment
          </Button>
        </form>
      </div>
      <div className="container mx-auto p-8">
        <h3 className="font-medium text-xl mb-7">Submitted Comments</h3>
        {isLoading ? (
          <div className="text-center text-gray-600">
            {' '}
            <LoadingMask />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {commentList?.length > 0 ? (
              commentList?.map(comment => (
                <div key={comment.timestamp} className="bg-white p-4 rounded-md shadow-md">
                  <p className="text-black-600 mb-2">Comment : {comment.comment}</p>
                  <p className="text-gray-500 tracking-wide text-sm">
                    Submitted On : {formatDateTimeCompact(comment.timestamp).toString()}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No submitted comments.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default colabComments;
